import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  TrendingUp, Shield, Zap, ChevronRight, Star, 
  ArrowUpRight, Plus, Minus, DollarSign, Lock, BarChart3, Wallet
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CryptoTicker from '../components/CryptoTicker';
import TrustBadges from '../components/TrustBadges';
import { useAuth } from '../context/AuthContext';
import { initData, getPlatformSettings } from '../services/api';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="glass-card mb-4 overflow-hidden">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-6 text-left"
    >
      <span className="font-semibold text-white pr-4">{question}</span>
      {isOpen ? <Minus className="w-5 h-5 text-cyan-400 flex-shrink-0" /> : <Plus className="w-5 h-5 text-slate-400 flex-shrink-0" />}
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <p className="px-6 pb-6 text-slate-400 leading-relaxed">{answer}</p>
    </motion.div>
  </div>
);

const HomePage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [settings, setSettings] = useState({});
  const { user, isAuthenticated, loading } = useAuth();

  useEffect(() => {
    const init = async () => {
      try {
        await initData();
        const settingsRes = await getPlatformSettings();
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Failed to initialize:', error);
      }
    };
    init();
  }, []);

  const dailyProfit = settings.daily_profit_percentage || '3';

  const features = [
    {
      icon: DollarSign,
      title: 'Daily Returns',
      description: `Earn up to ${dailyProfit}% daily returns through our fully automated trading strategies.`
    },
    {
      icon: Shield,
      title: 'Secure & Transparent',
      description: 'Your funds are protected with enterprise-grade security and full transparency on all transactions.'
    },
    {
      icon: Zap,
      title: 'Instant Activation',
      description: 'Deposits are automatically activated for trading once confirmed. No manual steps required.'
    },
    {
      icon: Lock,
      title: 'Fully Automated',
      description: 'Our trading engine works 24/7 so you don\'t have to. Just deposit and start earning.'
    }
  ];

  const stats = [
    { label: 'Active Traders', value: 25000, suffix: '+' },
    { label: 'Total Profits Distributed', value: 12, prefix: '$', suffix: 'M+' },
    { label: 'Daily Return Rate', value: parseFloat(dailyProfit), suffix: '%' },
    { label: 'Satisfied Users', value: 98, suffix: '%' }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Create Account',
      description: 'Sign up in minutes with just your email. Your account is verified instantly.',
      icon: '👤'
    },
    {
      step: '02',
      title: 'Make a Deposit',
      description: 'Deposit funds using BTC, ETH, or USDT. Minimum deposit starts from $50.',
      icon: '💰'
    },
    {
      step: '03',
      title: 'Trading Activates',
      description: 'Once your deposit is confirmed, automated trading begins immediately. No manual action needed.',
      icon: '📈'
    },
    {
      step: '04',
      title: 'Earn Daily Profits',
      description: `Your funds generate ${dailyProfit}% daily returns, automatically added to your trading balance.`,
      icon: '💎'
    }
  ];

  const testimonials = [
    {
      name: 'Michael Chen',
      role: 'Investor since 2023',
      content: 'MetaBlockify has completely changed my approach to crypto trading. The daily returns are consistent and the platform is incredibly easy to use.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100'
    },
    {
      name: 'Sarah Johnson',
      role: 'Business Owner',
      content: 'I was skeptical at first, but the transparent operations and reliable returns convinced me. Best trading decision I made this year!',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100'
    },
    {
      name: 'David Park',
      role: 'Software Engineer',
      content: 'The security features and professional support team make MetaBlockify stand out. My portfolio has grown significantly.',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100'
    }
  ];

  const faqs = [
    {
      question: 'How does MetaBlockify work?',
      answer: 'MetaBlockify uses advanced automated trading strategies to generate consistent returns. Once your deposit is confirmed, funds are automatically activated for trading. Profits are calculated daily and added to your account.'
    },
    {
      question: 'What is the minimum deposit?',
      answer: 'The minimum deposit to start is $50. This allows you to experience our platform and returns before committing larger amounts.'
    },
    {
      question: 'How are profits distributed?',
      answer: `Profits are calculated daily at a rate of ${dailyProfit}% and automatically added to your trading balance. You can withdraw your profits at any time.`
    },
    {
      question: 'Is my money safe?',
      answer: 'Yes, we use enterprise-grade security measures including encryption, secure wallets, and regular security audits. Your funds are held securely and you maintain full control over withdrawals.'
    },
    {
      question: 'How do I withdraw my funds?',
      answer: 'Withdrawals can be made at any time through your dashboard. Simply enter your wallet address and the amount you wish to withdraw. Processing typically takes 24-48 hours.'
    }
  ];

  return (
    <div className="min-h-screen">
      <div className="animated-bg" />
      <div className="noise-overlay" />
      <Navbar />

      {/* Hero Section */}
      <section className="hero-section pt-32" data-testid="hero-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto text-center px-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-8">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-sm text-cyan-400 font-medium">Automated Trading Platform</span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold font-['Outfit'] mb-6 leading-tight">
            <span className="text-white">Grow Your Wealth</span>
            <br />
            <span className="gradient-text">With Crypto</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            Earn up to <span className="text-emerald-400 font-bold">{dailyProfit}% daily returns</span> through fully automated crypto trading. 
            Join 25,000+ traders already profiting with our secure platform.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {isAuthenticated && user ? (
              <>
                <Link to="/dashboard" className="btn-primary flex items-center gap-2" data-testid="hero-cta">
                  Go to Dashboard
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/deposit" className="btn-secondary flex items-center gap-2">
                  Deposit Now
                  <Wallet className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <>
                <Link to="/register" className="btn-primary flex items-center gap-2" data-testid="hero-cta">
                  Start Trading Now
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/faq" className="btn-secondary flex items-center gap-2">
                  Learn More
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-8 text-slate-400">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              <span className="text-sm">256-bit Encryption</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
              <span className="text-sm">{dailyProfit}% Daily Returns</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              <span className="text-sm">4.9/5 Rating</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Crypto Ticker */}
      <section className="px-4 sm:px-6 -mt-6 sm:-mt-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <CryptoTicker />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="stat-card text-center"
              >
                <div className="text-3xl sm:text-4xl font-bold font-['Outfit'] gradient-text mb-2">
                  <AnimatedCounter 
                    end={stat.value} 
                    prefix={stat.prefix || ''} 
                    suffix={stat.suffix || ''} 
                  />
                </div>
                <p className="text-slate-400 text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] text-white mb-4">
              Why Choose MetaBlockify?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We provide the tools and expertise you need to succeed in cryptocurrency trading.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 group"
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] text-white mb-4">
              How It Works
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Start earning passive income in just 4 simple steps.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 relative overflow-hidden group"
              >
                <div className="absolute top-4 right-4 text-6xl font-bold text-white/5 font-['Outfit']">
                  {item.step}
                </div>
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Profit Calculator */}
      <section className="py-20 px-6" data-testid="profit-calculator-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="glass-card p-8 md:p-12 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-emerald-500/5" />
            <div className="relative">
              <div className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] text-white mb-4">
                  Profit Calculator
                </h2>
                <p className="text-slate-400">
                  See how much you could earn with MetaBlockify
                </p>
              </div>

              <ProfitCalculator dailyProfit={dailyProfit} isAuthenticated={isAuthenticated} user={user} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] text-white mb-4">
              What Our Traders Say
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Join thousands of satisfied traders who trust MetaBlockify.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-slate-300 leading-relaxed mb-6">"{testimonial.content}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-slate-400">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-400">
              Everything you need to know about MetaBlockify.
            </p>
          </motion.div>

          <div>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="glass-card p-12 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-emerald-500/10" />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] text-white mb-4">
                Ready to Start Trading?
              </h2>
              <p className="text-slate-400 mb-8 max-w-xl mx-auto">
                Join over 25,000 traders who are already earning {dailyProfit}% daily returns with MetaBlockify. 
                Start your trading journey today.
              </p>
              <Link 
                to={isAuthenticated && user ? "/dashboard" : "/register"} 
                className="btn-primary inline-flex items-center gap-2"
              >
                {isAuthenticated && user ? "Go to Dashboard" : "Create Free Account"}
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ProfitCalculator = ({ dailyProfit, isAuthenticated, user }) => {
  const [depositAmount, setDepositAmount] = useState(1000);
  const [periodDays, setPeriodDays] = useState(30);

  const rate = parseFloat(dailyProfit) / 100;
  const dailyReturn = depositAmount * rate;
  const totalProfit = dailyReturn * periodDays;
  const totalValue = depositAmount + totalProfit;
  const roiPercent = (totalProfit / depositAmount * 100).toFixed(1);

  const periodOptions = [
    { days: 7, label: '1 Week' },
    { days: 30, label: '1 Month' },
    { days: 90, label: '3 Months' },
    { days: 180, label: '6 Months' },
    { days: 365, label: '1 Year' },
  ];

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

  return (
    <div className="space-y-8">
      {/* Investment Amount Slider */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-slate-300">Deposit Amount</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <input
              type="number"
              value={depositAmount}
              onChange={(e) => setDepositAmount(Math.max(50, Math.min(1000000, parseFloat(e.target.value) || 0)))}
              className="bg-white/5 border border-white/10 rounded-lg pl-7 pr-3 py-2 text-white text-sm w-32 text-right focus:outline-none focus:border-cyan-500/50"
              data-testid="calc-deposit-input"
            />
          </div>
        </div>
        <input
          type="range"
          min="50"
          max="100000"
          step="50"
          value={depositAmount}
          onChange={(e) => setDepositAmount(parseInt(e.target.value))}
          className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer accent-cyan-500"
          style={{ accentColor: '#06b6d4' }}
          data-testid="calc-deposit-slider"
        />
        <div className="flex justify-between text-xs text-slate-500 mt-1">
          <span>$50</span>
          <span>$100,000</span>
        </div>
      </div>

      {/* Period Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-3">Trading Period</label>
        <div className="flex flex-wrap gap-2" data-testid="calc-period-buttons">
          {periodOptions.map((p) => (
            <button
              key={p.days}
              onClick={() => setPeriodDays(p.days)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                periodDays === p.days
                  ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/25'
                  : 'bg-white/5 text-slate-400 border border-white/[0.06] hover:text-white hover:border-white/20'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="calc-results">
        <div className="p-4 bg-white/5 rounded-xl text-center">
          <p className="text-slate-400 text-xs mb-1">Deposited</p>
          <p className="text-xl font-bold text-white">{formatCurrency(depositAmount)}</p>
        </div>
        <div className="p-4 bg-emerald-500/10 rounded-xl text-center border border-emerald-500/20">
          <p className="text-slate-400 text-xs mb-1">Est. Profit</p>
          <p className="text-xl font-bold text-emerald-400" data-testid="calc-total-profit">+{formatCurrency(totalProfit)}</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-cyan-500/10 to-emerald-500/10 rounded-xl text-center border border-cyan-500/20">
          <p className="text-slate-400 text-xs mb-1">Total Value</p>
          <p className="text-xl font-bold text-white">{formatCurrency(totalValue)}</p>
        </div>
        <div className="p-4 bg-white/5 rounded-xl text-center">
          <p className="text-slate-400 text-xs mb-1">ROI</p>
          <p className="text-xl font-bold text-cyan-400">{roiPercent}%</p>
        </div>
      </div>

      {/* Daily breakdown */}
      <div className="text-center text-sm text-slate-400">
        Earning <span className="text-emerald-400 font-semibold">{formatCurrency(dailyReturn)}</span> per day at {dailyProfit}% daily rate
      </div>

      <div className="text-center">
        <Link
          to={isAuthenticated && user ? "/deposit" : "/register"}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="calc-cta-button"
        >
          {isAuthenticated && user ? "Deposit Now" : "Start Earning Now"}
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

export default HomePage;
